// src/components/dashboard/RecentActivity.jsx
import React, { useState } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "components/ui/card";
import { Badge } from "components/ui/badge";
import {
  Clock,
  Search as SearchIcon,
  Map as MapIcon,
  AlertTriangle,
  Target,
  ArrowRight,
} from "lucide-react";
import { format } from "date-fns";
import { Skeleton } from "components/ui/skeleton";

export default function RecentActivity({
  problems = [],
  roadmapItems = [],
  issues = [],
  hypotheses = [],
  isLoading,
}) {
  const [hovered, setHovered] = useState(null);

  const buildActivity = () => {
    const activity = [];

    // Problems
    problems.slice(0, 3).forEach((item) => {
      activity.push({
        id: item.id,
        type: "problem",
        title: item.title,
        status: item.status,
        date: item.updated_date,
        Icon: SearchIcon,
      });
    });

    // Roadmap items
    roadmapItems.slice(0, 3).forEach((item) => {
      activity.push({
        id: item.id,
        type: "roadmap",
        title: item.title,
        status: item.status,
        date: item.updated_date,
        Icon: MapIcon,
      });
    });

    // Critical issues
    issues
      .filter((i) => i.tier === "tier1")
      .slice(0, 2)
      .forEach((item) => {
        activity.push({
          id: item.id,
          type: "issue",
          title: item.title,
          status: item.status,
          date: item.created_date,
          Icon: AlertTriangle,
        });
      });

    // Hypotheses
    hypotheses.slice(0, 2).forEach((item) => {
      activity.push({
        id: item.id,
        type: "hypothesis",
        title: item.statement,
        status: item.status,
        date: item.updated_date,
        Icon: Target,
      });
    });

    return activity
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 8);
  };

  const activity = buildActivity();

  const getBadgeStyle = (type, status) => {
    const map = {
      problem: {
        discovery: { bg: "var(--accent-soft)", color: "var(--accent-fg)" }, // blue
        validated: { bg: "var(--success-soft)", color: "var(--success)" }, // green
        in_roadmap: { bg: "var(--accent-soft)", color: "var(--accent-fg)" }, // purple
      },
      roadmap: {
        planned: { bg: "var(--bg-subtle)", color: "var(--fg-secondary)" }, // slate
        in_development: { bg: "var(--warning-soft)", color: "var(--warning)" }, // orange
        launched: { bg: "var(--success-soft)", color: "var(--success)" }, // green
      },
      issue: {
        new: { bg: "var(--danger-soft)", color: "var(--danger-fg)" }, // red
        triaged: { bg: "var(--warning-soft)", color: "var(--warning)" }, // yellow
        resolved: { bg: "var(--success-soft)", color: "var(--success)" }, // green
      },
      hypothesis: {
        draft: { bg: "var(--bg-subtle)", color: "var(--fg-secondary)" }, // slate
        testing: { bg: "var(--accent-soft)", color: "var(--accent-fg)" }, // blue
        validated: { bg: "var(--success-soft)", color: "var(--success)" }, // green
        invalidated: { bg: "var(--danger-soft)", color: "var(--danger-fg)" }, // red
      },
    };
    return map[type]?.[status] ?? { bg: "var(--bg-subtle)", color: "var(--fg-secondary)" };
  };

  const typeLabel = (type) =>
    ({ problem: "Problem", roadmap: "Roadmap", issue: "Issue", hypothesis: "Hypothesis" }[type] ||
      type);

  const safeFormat = (d) => {
    const dt = new Date(d);
    if (isNaN(dt.getTime())) return "";
    return format(dt, "MMM d, h:mm a");
    // If you prefer 24h: format(dt, "MMM d, HH:mm")
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle style={{ display: "flex", alignItems: "center", gap: 8, color: "var(--fg)" }}>
          <Clock size={20} color="var(--fg-secondary)" />
          Recent Activity
        </CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div style={{ display: "grid", gap: 12 }}>
            {Array.from({ length: 6 }).map((_, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 12 }}>
                <Skeleton style={{ width: 32, height: 32, borderRadius: 8 }} />
                <div style={{ flex: 1 }}>
                  <Skeleton style={{ height: 16, width: "100%", marginBottom: 6 }} />
                  <Skeleton style={{ height: 12, width: 100 }} />
                </div>
              </div>
            ))}
          </div>
        ) : activity.length > 0 ? (
          <div style={{ display: "grid", gap: 8 }}>
            {activity.map((item, idx) => {
              const palette = getBadgeStyle(item.type, item.status);
              const active = hovered === idx;
              return (
                <div
                  key={`${item.type}-${item.id}`}
                    onMouseEnter={() => setHovered(idx)}
                    onMouseLeave={() => setHovered(null)}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 12,
                    padding: 12,
                    borderRadius: 10,
                    background: active ? "var(--bg-inset)" : "transparent",
                    transition: "background 160ms",
                  }}
                >
                  <div
                    style={{
                      padding: 8,
                      borderRadius: 10,
                      background: active ? "var(--border)" : "var(--bg-subtle)",
                      transition: "background 160ms",
                      display: "grid",
                      placeItems: "center",
                    }}
                  >
                    <item.Icon size={16} color="var(--fg-secondary)" />
                  </div>

                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4 }}>
                      <span
                        style={{
                          fontSize: 12,
                          textTransform: "uppercase",
                          color: "var(--fg-muted)",
                          fontWeight: 600,
                        }}
                      >
                        {typeLabel(item.type)}
                      </span>
                      <Badge
                        className="text-xs"
                        style={{
                          background: palette.bg,
                          color: palette.color,
                          border: "0",
                          fontSize: 12,
                        }}
                      >
                        {(item.status || "").replace(/_/g, " ")}
                      </Badge>
                    </div>

                    <p
                      style={{
                        margin: 0,
                        fontSize: 14,
                        fontWeight: 600,
                        color: active ? "var(--fg)" : "var(--fg)",
                        whiteSpace: "nowrap",
                        overflow: "hidden",
                        textOverflow: "ellipsis",
                      }}
                      title={item.title}
                    >
                      {item.title}
                    </p>

                    <p style={{ margin: 0, fontSize: 12, color: "var(--fg-muted)" }}>
                      {safeFormat(item.date)}
                    </p>
                  </div>

                  <ArrowRight
                    size={16}
                    color="var(--border-strong)"
                    style={{ opacity: active ? 1 : 0, transition: "opacity 160ms" }}
                  />
                </div>
              );
            })}
          </div>
        ) : (
          <div style={{ textAlign: "center", padding: "24px 0" }}>
            <Clock size={48} color="var(--border-strong)" style={{ marginBottom: 8 }} />
            <p style={{ margin: 0, color: "var(--fg-muted)" }}>No recent activity</p>
            <p style={{ margin: 0, marginTop: 4, fontSize: 12, color: "var(--fg-subtle)" }}>
              Start creating problems and initiatives
            </p>
          </div>
        )}
      </CardContent>
    </Card>
  );
}
